import c from "classnames";
import { PropsWithChildren, memo, useMemo } from "react";

import classes from "./Code.module.css";

type Size = 8 | 10 | 11 | 12 | 14 | 16 | 18 | 21 | 24 | 32 | 40 | 48 | 56 | 64;
type Weight = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;
type Color =
  | "textDark"
  | "textLight"
  | "textSecondary"
  | "white"
  | "primary"
  | "danger"
  | "inherit";
type Spacing = "normal" | "tight" | "relaxed";
type Display = "inline" | "block";

type Props = PropsWithChildren<{
  size?: Size;
  weight?: Weight;
  color?: Color;
  spacing?: Spacing;
  uppercase?: boolean;
  clamp?: boolean;
  truncate?: boolean;
  maxWidth?: number;
  title?: string;
  display?: Display;
  language?: string;
}>;

export const Code = memo(
  ({
    size = 14,
    weight = 400,
    color = "textDark",
    spacing = "normal",
    uppercase,
    clamp,
    truncate,
    children,
    maxWidth,
    title,
    display = "inline",
    language,
    ...rest
  }: Props) => {
    const styles = useMemo(() => {
      return {
        fontSize: `${size}px`,
        fontWeight: weight,
        maxWidth: maxWidth ? `${maxWidth}px` : undefined,
      };
    }, [size, weight, maxWidth]);

    const content = typeof children === "string" ? children.trim() : children;

    return (
      <code
        className={c(
          classes.code,
          classes[color],
          classes[spacing],
          classes[display],
          uppercase && classes.uppercase,
          clamp && classes.clamp,
          truncate && classes.truncate,
        )}
        title={
          title ? title : typeof children === "string" ? children : undefined
        }
        style={styles}
        data-language={language}
        {...rest}
      >
        {content}
      </code>
    );
  },
);

Code.displayName = "Code";
